import * as z from "zod";

export const WatchdogVerdict = z.enum([
  "healthy",
  "suspicious",
  "regressed",
  "inconclusive",
]);
export type WatchdogVerdict = z.infer<typeof WatchdogVerdict>;

export const WatchdogRunStatus = z.enum([
  "planning",
  "watching",
  "finished",
  "aborted",
]);
export type WatchdogRunStatus = z.infer<typeof WatchdogRunStatus>;

const WatchdogWatch = z.object({
  id: z.string(),
  signal: z.string(),
  source: z.string(),
  expectation: z.enum(["should_change", "should_not_change"]),
  threshold: z.string(),
});
export type WatchdogWatch = z.infer<typeof WatchdogWatch>;

const WatchdogObservation = z.object({
  id: z.string(),
  watch_id: z.string(),
  summary: z.string(),
  status: z.enum(["ok", "anomalous"]),
  evidence_url: z.string().nullable(),
  sub_session_id: z.string().nullable(),
  observed_at: z.string(),
});
export type WatchdogObservation = z.infer<typeof WatchdogObservation>;

/** The backend's WatchdogRunSnapshot: `watchdog_run_updated` frames and the session payload's `watchdog_runs`. */
export const WatchdogRunSnapshot = z.object({
  /** The watchdog session that owns the run. */
  child_session_id: z.string(),
  run_id: z.string(),
  status: WatchdogRunStatus,
  verdict: WatchdogVerdict.nullable(),
  summary: z.string().nullable(),
  verdict_observation_ids: z.array(z.string()),
  aborted_reason: z.string().nullable(),
  started_at: z.string().nullable(),
  window_ends_at: z.string(),
  finished_at: z.string().nullable(),
  /** Bumped on every write: a frame or payload with a lower revision than the held run is stale. */
  revision: z.number().int(),
  release: z.object({
    source: z.string(),
    source_release_id: z.string().nullable(),
    repository: z.string().nullable(),
    service: z.string().nullable(),
    environment: z.string().nullable(),
    version: z.string().nullable(),
    head_sha: z.string().nullable(),
    base_sha: z.string().nullable(),
    is_rollback: z.boolean(),
    status: z.string().nullable(),
    url: z.string().nullable(),
  }),
  watch_plan: z
    .object({
      watches: z.array(WatchdogWatch),
      unwatched: z.array(z.object({ signal: z.string(), reason: z.string() })),
    })
    .nullable(),
  /** The latest observation of each watch. */
  observations: z.array(WatchdogObservation),
  investigations: z.array(
    z.object({ session_id: z.string(), title: z.string().nullable() }),
  ),
});
export type WatchdogRunSnapshot = z.infer<typeof WatchdogRunSnapshot>;

/** A `watchdog_run_updated` frame; a `partial` one was too large for the socket and only names the run to refetch. */
export const WatchdogRunFrame = z.discriminatedUnion("partial", [
  WatchdogRunSnapshot.extend({ partial: z.literal(false) }),
  z.object({
    partial: z.literal(true),
    child_session_id: z.string(),
    run_id: z.string(),
  }),
]);
export type WatchdogRunFrame = z.infer<typeof WatchdogRunFrame>;
